import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeft, Trash2, Copy, Download, FileText, BookOpen, Presentation, ScrollText, StickyNote, Lightbulb, Loader2, Layers, ClipboardList } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import MarkdownContent from '@/components/MarkdownContent';
import SEO from '@/components/SEO';

interface ProcessedNote {
  id: string;
  file_name: string;
  processing_type: string;
  result: string;
  created_at: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  extract: <FileText className="w-5 h-5" />,
  assignment: <BookOpen className="w-5 h-5" />,
  presentation: <Presentation className="w-5 h-5" />,
  'long-notes': <ScrollText className="w-5 h-5" />,
  'short-notes': <StickyNote className="w-5 h-5" />,
  explain: <Lightbulb className="w-5 h-5" />,
  flashcards: <Layers className="w-5 h-5" />,
  quiz: <ClipboardList className="w-5 h-5" />,
};

const typeLabels: Record<string, string> = {
  extract: 'Text Extraction',
  assignment: 'Assignment Answers',
  presentation: 'Presentation',
  'long-notes': 'Detailed Notes',
  'short-notes': 'Quick Notes',
  explain: 'Explanation',
  flashcards: 'Flashcards',
  quiz: 'Quiz',
};

const MyNotes = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notes, setNotes] = useState<ProcessedNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState<ProcessedNote | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchNotes();
    }
  }, [user]);

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('processed_notes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast({ title: 'Failed to load notes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('processed_notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setNotes(notes.filter(n => n.id !== id));
      if (selectedNote?.id === id) setSelectedNote(null);
      toast({ title: 'Note deleted' });
    } catch (error) {
      toast({ title: 'Failed to delete note', variant: 'destructive' });
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard!' });
  };

  const handleDownload = (note: ProcessedNote) => {
    const blob = new Blob([note.result], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${note.file_name}-${note.processing_type}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="My Study Notes — NoteGenius"
        description="View, copy, download, and manage all your AI-processed study notes, flashcards, and summaries in one place."
        path="/my-notes"
      />
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg text-foreground">My Notes</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <h1 className="sr-only">My Study Notes</h1>
        {notes.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">No notes yet</h2>
            <p className="text-muted-foreground mb-6">
              Upload and process your first notes to see them here.
            </p>
            <Button variant="hero" onClick={() => navigate('/')}>
              Upload Notes
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Notes list */}
            <div className="md:col-span-1 space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  onClick={() => setSelectedNote(note)}
                  className={`glass rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedNote?.id === note.id ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      {typeIcons[note.processing_type] || <FileText className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{note.file_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {typeLabels[note.processing_type] || note.processing_type}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Note preview */}
            <div className="md:col-span-2">
              {selectedNote ? (
                <div className="glass rounded-2xl p-6 sticky top-24">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-foreground">{selectedNote.file_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {typeLabels[selectedNote.processing_type]}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleCopy(selectedNote.result)}>
                        <span className="sr-only">Copy note to clipboard</span>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownload(selectedNote)}>
                        <span className="sr-only">Download note</span>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(selectedNote.id)}>
                        <span className="sr-only">Delete note</span>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 md:p-6 max-h-[60vh] overflow-y-auto">
                    <MarkdownContent content={selectedNote.result} />
                  </div>
                </div>
              ) : (
                <div className="glass rounded-2xl p-12 text-center">
                  <p className="text-muted-foreground">Select a note to preview</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyNotes;
